import React, { ReactNode, useEffect, useState } from 'react';
import Box, { BoxProps } from 'ui-box';
import { CSSTransition } from 'react-transition-group';
import { majorScale } from '.';
import { Button } from './Button';
import { Card } from './Card';
import { defaultColors, defaultFills } from './Colors';
import { Heading } from './Heading';
import { Portal } from './Portal';

export interface IDialogProps extends Omit<BoxProps<'div'>, 'title'> {
  title?: string | ReactNode;
  isShown?: boolean;
  actions?: ReactNode;
  onClose: () => void;
  onOpened?: () => void;
  closesOnEscape?: boolean;
  maxWidth?: string | number;
  full?: boolean;
}

export const Dialog: React.FC<IDialogProps> = ({
  title,
  isShown = false,
  actions,
  maxWidth,
  full = false,
  onClose,
  onOpened,
  children,
  closesOnEscape = true,
  ...props
}) => {
  const overlayRef = React.useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    setShown(isShown);
  }, [isShown]);

  /**
   * Handle escape press; should close on escale
   */
  useEffect(() => {
    if (!closesOnEscape) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closesOnEscape, onClose]);

  return (
    <Portal>
      <CSSTransition
        in={shown}
        timeout={150}
        className="dialog-container"
        onExited={() => {
          onClose();
        }}
        onEntered={() => {
          onOpened && onOpened();
        }}
        unmountOnExit={true}
        nodeRef={overlayRef}
      >
        <div ref={overlayRef}>
          <div
            className="overlay"
            onClick={() => closesOnEscape && setShown(false)}
          />
          <Card elevation={3} className="dialog" maxWidth={maxWidth}>
            {title && (
              <Box className="header">
                <Heading size={400} width="100%">
                  {title}
                </Heading>
                <Button
                  iconLeft="cross"
                  flexShrink={0}
                  minimal={true}
                  height={32}
                  onClick={() => setShown(false)}
                />
              </Box>
            )}
            <Box
              backgroundColor={defaultColors.gray[50]}
              {...props}
              overflow="auto"
              maxHeight={`calc(100vh - ${majorScale(8 + 8 + 8)}px)`}
              height={
                full ? `calc(100vh - ${majorScale(8 + 8 + 8)}px)` : props.height
              }
            >
              {children}
            </Box>
            {actions && <Box className="actions">{actions}</Box>}
          </Card>
        </div>
      </CSSTransition>
    </Portal>
  );
};

export type ISideSheetProps = IDialogProps & {
  width?: number | string;
  showOverlay?: boolean;
};

export const SideSheet: React.FC<ISideSheetProps> = ({
  title,
  isShown = false,
  actions,
  onClose,
  onOpened,
  children,
  closesOnEscape = true,
  width = '100%',
  showOverlay = false,
  ...props
}) => {
  const containerRef = React.useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    setShown(isShown);
  }, [isShown]);

  return (
    <Portal>
      <CSSTransition
        in={shown}
        timeout={150}
        className="dialog-container side-sheet"
        onExited={() => {
          onClose();
        }}
        onEntered={() => {
          onOpened && onOpened();
        }}
        unmountOnExit={true}
        nodeRef={containerRef}
      >
        <div ref={containerRef}>
          {showOverlay && (
            <div className="overlay" onClick={() => setShown(false)} />
          )}
          <Box className="dialog sheet" minWidth={width} maxWidth={width}>
            <Box className="header">
              <Button
                marginLeft="0 !important"
                iconLeft="cross"
                flexShrink={0}
                minimal={true}
                height={32}
                onClick={() => setShown(false)}
              />
              <Box
                borderRight={`${defaultFills.normal[200]} 2px solid`}
                height={majorScale(2)}
                marginRight={majorScale(2)}
                marginLeft={majorScale(1)}
              />
              <Heading size={400} width="100%">
                {title}
              </Heading>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexShrink={0}
              >
                {actions}
              </Box>
            </Box>
            <Box
              backgroundColor={defaultColors.gray[50]}
              {...props}
              overflowY="scroll"
              className="content"
            >
              {children}
            </Box>
          </Box>
        </div>
      </CSSTransition>
    </Portal>
  );
};
